import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getDocs, collection, where, query } from "firebase/firestore";
import { devLog } from "../utils/devLog";
import { HABI_API_BASE_URL } from "../utils/habiApi";
import { getLastRoleDocId } from "../utils/roleSwitch";
import { colors, spacing, typography, transitions } from "../utils/designTokens";
import logo from "../assets/logo.png";
import habitiaAnimation from "../assets/habitia-animation.mp4";

// "Olvidé mi contraseña" sin enumeración: /api/request-password-reset responde
// lo mismo ("sent") haya o no una cuenta con ese email, y la pantalla tampoco
// lo averigua por su cuenta (antes buscaba el email en `users` y llamaba a
// fetchSignInMethodsForEmail, y así cualquiera sabía qué emails tenían cuenta y
// cuáles entraban con Google). Por eso el texto es uno solo. A una cuenta de
// Google sin contraseña el backend le explica por email cómo entrar.
const MSG_RESET_ENVIADO =
  "Si tu email tiene una cuenta en Habitia, te enviamos un enlace para crear una contraseña nueva. Revisá también el correo no deseado.";
// Va siempre debajo del anterior (no dice nada del email): quien todavía no
// activó su cuenta no recibe enlace, tiene que usar el de activación.
const MSG_RESET_SIN_ACTIVAR =
  "¿Todavía no activaste tu cuenta? Usá el enlace de activación que te llegó por email o pedíselo a la administración de tu edificio.";
const MSG_RESET_SIN_EMAIL = "Ingresá tu correo para recuperar la contraseña.";
const MSG_RESET_EMAIL_INVALIDO = "El correo ingresado no es válido. Por favor, verificá que esté bien escrito.";
const MSG_RESET_FALLO =
  "No pudimos enviar el enlace en este momento. Reintentá en unos segundos; si sigue sin funcionar, escribile a la administración de tu edificio por WhatsApp.";
// 429 del cupo de las rutas públicas de propia_agent (RateLimitMiddleware): no
// es un fallo, hay que esperar. No depende de que el email tenga cuenta.
const MSG_RESET_ESPERAR = "Recibimos varios pedidos seguidos desde tu conexión. Probá de nuevo en unos minutos.";

// El mismo formato que valida /api/request-password-reset.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "no_account" y "google" los devolvía el backend anterior: mientras conviva
// con este front se muestran con el mismo texto que "sent".
const RESPUESTAS_RESET_ENVIADO = new Set(["sent", "no_account", "google"]);

// "Ingresar" sin enumeración: email sin cuenta, contraseña incorrecta y
// credencial inválida (lo que responde Firebase con la protección contra la
// enumeración de emails activa) muestran el MISMO texto. Antes "No existe una
// cuenta con este correo." y "Contraseña incorrecta." decían qué emails tenían
// cuenta. El resto de los errores no dependen de que la cuenta exista (salvo
// "deshabilitada", que queda como estaba).
const MSG_LOGIN_CREDENCIALES = "Correo o contraseña incorrectos.";
const MSG_LOGIN_ERROR = "Error al iniciar sesión. Intenta nuevamente.";
// BUG-89: mensajes según el error de Firebase Auth.
const ERRORES_LOGIN = {
  "auth/invalid-email": "El correo electrónico no es válido.",
  "auth/user-disabled": "Esta cuenta ha sido deshabilitada.",
  "auth/user-not-found": MSG_LOGIN_CREDENCIALES,
  "auth/wrong-password": MSG_LOGIN_CREDENCIALES,
  "auth/invalid-credential": MSG_LOGIN_CREDENCIALES,
  "auth/too-many-requests": "Demasiados intentos. Intenta de nuevo más tarde.",
  "auth/network-request-failed": "Error de red. Verifica tu conexión a internet.",
};

// Inyectar estilos de animación
const injectAnimationStyles = () => {
  if (document.getElementById('habitia-login-animations')) return;
  
  const styleSheet = document.createElement('style');
  styleSheet.id = 'habitia-login-animations';
  styleSheet.textContent = `
    @keyframes containerFadeOut {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
    
    @keyframes patternFloat {
      0% {
        transform: translate(0, 0);
      }
      100% {
        transform: translate(80px, 80px);
      }
    }
    
    .habitia-transition-container {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: white;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    }
    
    .habitia-transition-container.fade-out {
      animation: containerFadeOut 0.5s ease-out forwards;
    }
    
    .habitia-video-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      max-width: 400px;
    }
    
    .habitia-video {
      width: 100%;
      height: auto;
      max-width: 350px;
    }
  `;
  document.head.appendChild(styleSheet);
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const videoRef = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [resetStatus, setResetStatus] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    injectAnimationStyles();
    if (sessionStorage.getItem("habitia_session_expired") === "true") {
      setSessionExpired(true);
      sessionStorage.removeItem("habitia_session_expired");
    }
  }, []);

  // Manejar el fin del video
  const handleVideoEnd = () => {
    setFadeOut(true);
    // Después del fade out, hacer login
    setTimeout(() => {
      if (pendingSession) {
        onLogin(pendingSession);
      }
    }, 500);
  };

  // Forzar login después de timeout de seguridad (si el video no termina)
  const forceLogin = () => {
    if (pendingSession && !fadeOut) {
      devLog("Timeout de seguridad: forzando login");
      setFadeOut(true);
      setTimeout(() => {
        onLogin(pendingSession);
      }, 300);
    }
  };

  // Reproducir video cuando se muestra la transición
  useEffect(() => {
    if (showTransition && videoRef.current) {
      // Timeout de seguridad: si el video no termina en 4 segundos, forzar login
      const safetyTimeout = setTimeout(forceLogin, 4000);
      
      videoRef.current.play().catch(err => {
        devLog("Error playing video:", err);
        // Si el video no puede reproducirse, hacer login inmediatamente
        clearTimeout(safetyTimeout);
        forceLogin();
      });

      return () => clearTimeout(safetyTimeout);
    }
  }, [showTransition, pendingSession, onLogin, fadeOut]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // 1) Autenticación
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const fbUser = cred.user;

      // 2) Traer el doc en "users" con ese email
      const userQuery = query(
        collection(db, "users"),
        where("email", "==", fbUser.email)
      );
      const snapshot = await getDocs(userQuery);

      if (snapshot.empty) {
        throw new Error("Usuario no encontrado en la colección 'users'");
      }

      // Multi-rol: un mismo email puede tener varios docs (uno por typeuser).
      // docs[0] era arbitrario (orden por doc ID) y un superadmin podía entrar
      // como residente. Preferimos la última plataforma usada (roleSwitch,
      // guardada por doc id) y, si no hay, el doc de mayor typeuser.
      const docsOrdenados = [...snapshot.docs].sort(
        (a, b) => Number(b.data()?.typeuser ?? 0) - Number(a.data()?.typeuser ?? 0)
      );
      let docSnap = docsOrdenados[0];
      const ultimoDocId = getLastRoleDocId(fbUser.email);
      if (ultimoDocId) {
        const preferido = docsOrdenados.find((d) => d.id === ultimoDocId);
        if (preferido) docSnap = preferido;
      }
      const data = docSnap.data();

      // 3) Armar sesión completa (IMPORTANTE: incluir userid)
      const session = {
        id: docSnap.id,                    // id del doc en Firestore
        email: fbUser.email,               // email de auth
        name: data?.name || "",
        userid: data?.userid != null && data?.userid !== "" ? Number(data.userid) : "",// <-- necesario para cargar apartments/userbuilding
        typeuser: Number(data?.typeuser ?? 0),
      };

      // 4) Mostrar animación de transición
      setPendingSession(session);
      setShowTransition(true);
      setLoading(false);
    } catch (err) {
      console.error("Error al iniciar sesión:", err);
      // Sin cuenta, contraseña incorrecta o credencial inválida: el mismo texto
      // (ver ERRORES_LOGIN).
      setError(ERRORES_LOGIN[err?.code] || MSG_LOGIN_ERROR);
      setLoading(false);
    }
  };

  // "Olvidé mi contraseña": todo pasa por /api/request-password-reset, el canal
  // CONFIABLE (habi@habitia.ai vía propia_agent), NO el sender de Firebase (que
  // a Hotmail le cae en spam/bloqueado y por eso "decía enviado" pero no
  // llegaba). La pantalla no consulta `users` ni los proveedores del email: la
  // respuesta es siempre la misma (ver MSG_RESET_ENVIADO).
  const handleResetPassword = async () => {
    if (resetLoading) return;
    setError("");
    setResetStatus("");
    const safeEmail = String(email || "").trim().toLowerCase();
    if (!safeEmail) {
      setError(MSG_RESET_SIN_EMAIL);
      return;
    }
    if (!EMAIL_RE.test(safeEmail)) {
      setError(MSG_RESET_EMAIL_INVALIDO);
      return;
    }
    setResetLoading(true);
    try {
      const resp = await fetch(`${HABI_API_BASE_URL}/api/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: safeEmail }),
      });
      const status = resp.ok ? (await resp.json())?.status : undefined;
      if (RESPUESTAS_RESET_ENVIADO.has(status)) {
        setResetStatus(MSG_RESET_ENVIADO);
      } else if (status === "invalid") {
        setError(MSG_RESET_EMAIL_INVALIDO);
      } else if (resp.status === 429) {
        setError(MSG_RESET_ESPERAR);
      } else {
        // HTTP con error, "error" del backend anterior o una respuesta
        // desconocida: no se mandó nada, así que no se le dice que sí. Tampoco
        // caemos al reset de Firebase (mail desde noreply@…firebaseapp.com,
        // sin marca Habitia y que suele ir a spam): si el servicio está frío,
        // un reintento lo despierta.
        console.warn("[Login] request-password-reset respondió:", resp.status, status);
        setError(MSG_RESET_FALLO);
      }
    } catch (err) {
      // Red caída o un cuerpo que no es JSON. El log no lleva el email.
      console.warn("[Login] request-password-reset no disponible:", err?.message || err);
      setError(MSG_RESET_FALLO);
    } finally {
      setResetLoading(false);
    }
  };

  // Mostrar pantalla de transición con video de animación del logo
  if (showTransition) {
    return (
      <div className={`habitia-transition-container ${fadeOut ? 'fade-out' : ''}`}>
        <div className="habitia-video-container">
          <video
            ref={videoRef}
            className="habitia-video"
            src={habitiaAnimation}
            muted
            playsInline
            onEnded={handleVideoEnd}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Fondo con patrón de logos */}
      <div style={styles.patternBackground}></div>

      {/* Overlay con gradiente */}
      <div style={styles.overlay}></div>

      {/* Card de login */}
      <div style={styles.card}>
        <img src={logo} alt="Logo" style={styles.logo} />
        {sessionExpired && (
          <div style={styles.sessionExpiredBanner} onClick={() => setSessionExpired(false)}>
            Tu sesión se cerró por inactividad. Por favor, ingresá nuevamente.
          </div>
        )}
        <h2 style={styles.title}>Iniciar sesión</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
          />
          {error && <p role="alert" style={styles.error}>{error}</p>}
          {resetStatus && (
            <div>
              <p role="status" style={styles.success}>{resetStatus}</p>
              <p style={styles.hint}>{MSG_RESET_SIN_ACTIVAR}</p>
            </div>
          )}
          <button
            type="button"
            style={{
              ...styles.linkButton,
              opacity: resetLoading ? 0.6 : 1,
              cursor: resetLoading ? "wait" : "pointer",
            }}
            onClick={handleResetPassword}
            disabled={loading || resetLoading}
          >
            {resetLoading ? "Enviando correo…" : "Olvidé mi contraseña"}
          </button>
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Ingresando..." : "Ingresar"}
          </button>
        </form>
      </div>
      {process.env.REACT_APP_FIREBASE_PROJECT_ID === "habitia-dev-8b512" && (
        <div style={{
          position: "absolute",
          bottom: 20,
          left: 0,
          right: 0,
          zIndex: 10,
          textAlign: "center",
          pointerEvents: "none",
        }}>
          <span style={{
            display: "inline-block",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            fontSize: 11,
            fontFamily: "'FT Habit', -apple-system, sans-serif",
            fontWeight: 600,
            borderRadius: 6,
            padding: "5px 16px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}>
            Ambiente de testing
          </span>
        </div>
      )}
    </div>
  );
};

// SVG del patrón de logos de Habitia (edificios blancos sobre fondo azul)
const createHabitiaPattern = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
      <rect fill="transparent" width="60" height="60"/>
      <!-- Edificio izquierdo -->
      <rect fill="white" x="5" y="0" width="8" height="60"/>
      <!-- Edificio central (cuadrado) -->
      <rect fill="white" x="21" y="24" width="8" height="12"/>
      <!-- Edificio derecho -->
      <rect fill="white" x="37" y="0" width="8" height="60"/>
    </svg>
  `;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const styles = {
  container: {
    backgroundColor: "#0080ff",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  patternBackground: {
    position: "absolute",
    top: "-60px",
    left: "-60px",
    right: "-60px",
    bottom: "-60px",
    backgroundImage: createHabitiaPattern(),
    backgroundRepeat: "repeat",
    backgroundSize: "60px 60px",
    opacity: 0.12,
    animation: "patternFloat 30s linear infinite",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(ellipse at center, rgba(0, 128, 255, 0.3) 0%, rgba(0, 100, 200, 0.5) 100%)",
    pointerEvents: "none",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "48px 40px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 25px 60px rgba(0, 0, 0, 0.3)",
    textAlign: "center",
    position: "relative",
    zIndex: 10,
  },
  logo: { 
    width: "200px", 
    marginBottom: "32px", 
    height: "auto",
  },
  title: { 
    fontSize: "24px", 
    marginBottom: "28px", 
    color: "#0080ff", 
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
  },
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  input: {
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    fontSize: "15px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f8f9fa",
    transition: "all 0.2s ease",
    outline: "none",
  },
  button: {
    backgroundColor: "#0080ff",
    color: "#fff",
    padding: "14px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "16px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: "all 0.2s ease",
    marginTop: "8px",
  },
  sessionExpiredBanner: {
    backgroundColor: "#FEF3C7",
    borderLeft: "4px solid #F59E0B",
    color: "#92400E",
    padding: "12px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    textAlign: "left",
    marginBottom: "16px",
    cursor: "pointer",
    lineHeight: "1.4",
  },
  // Avisos del formulario con tokens: error y éxito en sus tonos oscuros (AA
  // sobre blanco; #ef4444 y #10b981 a este tamaño no llegan a 4.5:1).
  error: { color: colors.errorDark, fontSize: typography.sizes.md, lineHeight: 1.5, margin: 0 },
  success: { color: colors.successDark, fontSize: typography.sizes.base, lineHeight: 1.5, margin: 0 },
  hint: {
    color: colors.gray600,
    fontSize: typography.sizes.base,
    lineHeight: 1.5,
    margin: `${spacing[2]}px 0 0`,
  },
  linkButton: {
    border: "none",
    background: "transparent",
    color: colors.primary,
    fontSize: typography.sizes.base,
    cursor: "pointer",
    padding: "2px 0",
    textAlign: "right",
    fontFamily: typography.fontFamily,
    transition: `opacity ${transitions.fast}`,
  },
};

export default Login;
